import React, { useState, useCallback, useRef, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Box,
  Button,
  ButtonGroup,
} from "@mui/material";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import RestoreIcon from "@mui/icons-material/Restore";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";

const ZoomableImageDialog = ({
  open,
  onClose,
  imageSrc,
  alt = "Image",
  title,
}) => {
  const { t } = useTranslation();
  const [zoom, setZoom] = useState(1);
  const [imgSize, setImgSize] = useState({ w: 0, h: 0 });
  const imgRef = useRef(null);

  const handleZoomIn = useCallback(() => {
    setZoom((z) => Math.min(z + 0.25, 4));
  }, []);

  const handleZoomOut = useCallback(() => {
    setZoom((z) => Math.max(z - 0.25, 0.25));
  }, []);

  const handleReset = useCallback(() => {
    setZoom(1);
  }, []);

  const handleClose = useCallback(() => {
    setZoom(1);
    onClose();
  }, [onClose]);

  const handleImageClick = useCallback((e) => {
    e.stopPropagation();
    if (e.detail === 2) {
      setZoom(1);
      return;
    }
    setZoom((z) => (z >= 4 ? 1 : Math.min(z + 0.25, 4)));
  }, []);

  const handleImageLoad = useCallback(() => {
    if (imgRef.current) {
      setImgSize({
        w: imgRef.current.naturalWidth,
        h: imgRef.current.naturalHeight,
      });
    }
  }, []);

  useEffect(() => {
    if (imageSrc) setImgSize({ w: 0, h: 0 });
  }, [imageSrc]);

  if (!imageSrc) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          maxWidth: "95vw",
          maxHeight: "95vh",
          width: "auto",
          height: "auto",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          py: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {title && (
            <Box component="span" sx={{ fontWeight: "bold" }}>
              {title}
            </Box>
          )}
          <ButtonGroup size="small" variant="outlined">
            <Button
              onClick={handleZoomIn}
              startIcon={<ZoomInIcon />}
              disabled={zoom >= 4}
            >
              {t("common.zoomIn", "Zoom in")}
            </Button>
            <Button
              onClick={handleZoomOut}
              startIcon={<ZoomOutIcon />}
              disabled={zoom <= 0.25}
            >
              {t("common.zoomOut", "Zoom out")}
            </Button>
            <Button onClick={handleReset} startIcon={<RestoreIcon />}>
              {t("common.reset", "Reset")}
            </Button>
          </ButtonGroup>
        </Box>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{
          overflow: "auto",
          p: 2,
          minHeight: 300,
          maxHeight: "75vh",
        }}
      >
        <Box
          sx={{
            display: "inline-block",
            cursor: zoom >= 4 ? "zoom-out" : "zoom-in",
          }}
          onClick={handleImageClick}
        >
          <img
            ref={imgRef}
            src={imageSrc}
            alt={alt}
            onLoad={handleImageLoad}
            style={{
              width: imgSize.w && imgSize.h ? imgSize.w * zoom : "100%",
              height: imgSize.w && imgSize.h ? imgSize.h * zoom : "auto",
              maxWidth: "none",
              objectFit: "contain",
              transition: "width 0.15s ease-out, height 0.15s ease-out",
              display: "block",
              verticalAlign: "top",
            }}
            draggable={false}
          />
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ZoomableImageDialog;
